import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X, FileText } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Default company info (used as fallback ONLY when /settings/company fails — blank to avoid misleading addresses).
const DEFAULT_COMPANY_INFO = {
  name: "BBH - Bosveld Bou & Hardeware",
  address: "",
  city: "",
  phone: "",
  email: "",
  vatNumber: "",
  logo: "/logo.png"
};

export const QuotationReceiptModal = ({ open, onClose, quotation }) => {
  const [receiptFormat, setReceiptFormat] = useState("a4");
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const printRef = useRef();

  // Fetch company settings when modal opens
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/company`);
        const settings = response.data;
        setCompanyInfo({
          name: settings.company_name || DEFAULT_COMPANY_INFO.name,
          address: settings.address_line1 || DEFAULT_COMPANY_INFO.address,
          city: [settings.city, settings.province, settings.postal_code].filter(Boolean).join(", ") || DEFAULT_COMPANY_INFO.city,
          phone: settings.phone || DEFAULT_COMPANY_INFO.phone,
          storeNumber: settings.fax || "",
          email: settings.email || DEFAULT_COMPANY_INFO.email,
          vatNumber: settings.vat_number ? `VAT No: ${settings.vat_number}` : DEFAULT_COMPANY_INFO.vatNumber,
          logo: settings.logo_url || DEFAULT_COMPANY_INFO.logo,
          footerLine1: settings.receipt_footer_line1 || "Thank you for your business!",
          footerLine2: settings.receipt_footer_line2 || "",
          footerLine3: settings.receipt_footer_line3 || ""
        });
      } catch (error) {
        console.error("Error fetching company settings:", error);
      }
    };

    if (open) {
      fetchCompanySettings();
    }
  }, [open]);

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .receipt { font-family: 'Courier New', monospace; }
        .quotation { font-family: Arial, sans-serif; }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 8px; text-align: left; }
        th { background-color: #eff6ff; }
        .border-b { border-bottom: 1px solid #e5e7eb; }
        .border-t { border-top: 1px solid #e5e7eb; }
        .border-dashed { border-style: dashed; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .font-bold { font-weight: bold; }
        .text-blue-600 { color: #2563eb; }
        .bg-blue-50 { background-color: #eff6ff; }
        .bg-gray-50 { background-color: #f9fafb; }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Quotation ${quotation?.quotation_number}</title>
          ${styles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!quotation) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValidUntil = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const hasCustomerDetails = quotation.customer_name || quotation.customer_account_number;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Quotation {quotation.quotation_number}
            </span>
            <div className="flex gap-2">
              <Button
                variant={receiptFormat === "thermal" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("thermal")}
              >
                58mm Thermal
              </Button>
              <Button
                variant={receiptFormat === "a4" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("a4")}
              >
                A4 Quote
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Print Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer className="h-4 w-4 mr-2" />
            Print {receiptFormat === "thermal" ? "Receipt" : "Quotation"}
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>

        {/* Quotation Preview */}
        <div className={`border rounded-lg p-4 ${receiptFormat === "thermal" ? "bg-white max-w-[250px] mx-auto" : "bg-white"}`}>
          <div ref={printRef}>
            {receiptFormat === "thermal" ? (
              <ThermalQuote quotation={quotation} formatDate={formatDate} formatValidUntil={formatValidUntil} hasCustomerDetails={hasCustomerDetails} companyInfo={companyInfo} />
            ) : (
              <A4Quote quotation={quotation} formatDate={formatDate} formatValidUntil={formatValidUntil} hasCustomerDetails={hasCustomerDetails} companyInfo={companyInfo} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// 58mm Thermal Quote Component
const ThermalQuote = ({ quotation, formatDate, formatValidUntil, hasCustomerDetails, companyInfo }) => {
  return (
    <div className="receipt font-mono text-xs" style={{ width: '58mm' }}>
      {/* Logo */}
      <div className="text-center mb-2">
        <img 
          src={companyInfo.logo} 
          alt={companyInfo.name}
          className="mx-auto"
          style={{ maxWidth: '45mm', maxHeight: '25mm', objectFit: 'contain' }}
        />
      </div>

      {/* Header */}
      <div className="text-center mb-2">
        <div className="text-[10px]">{companyInfo.address}</div>
        <div className="text-[10px]">{companyInfo.city}</div>
        <div className="text-[10px]">Tel: {companyInfo.phone}</div>
        {companyInfo.storeNumber && (
          <div className="text-[10px]">Store: {companyInfo.storeNumber}</div>
        )}
        <div className="text-[10px]">{companyInfo.vatNumber}</div>
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Quotation Info */}
      <div className="text-[10px]">
        <div className="text-center font-bold text-sm mb-1">QUOTATION</div>
        <div className="flex justify-between">
          <span>Quote No:</span>
          <span>{quotation.quotation_number}</span>
        </div>
        <div className="flex justify-between">
          <span>Date:</span>
          <span>{formatDate(quotation.created_at)}</span>
        </div>
        <div className="flex justify-between">
          <span>Valid Until:</span>
          <span>{formatValidUntil(quotation.valid_until)}</span>
        </div>
        <div className="flex justify-between">
          <span>Type:</span>
          <span>{quotation.quote_type}</span>
        </div>
      </div>

      {/* Customer Details */}
      {hasCustomerDetails && (
        <>
          <div className="border-t border-dashed border-black my-2"></div>
          <div className="text-[10px]">
            <div className="font-bold mb-1">CUSTOMER:</div>
            <div>{quotation.customer_name}</div>
            {quotation.customer_account_number && (
              <div>A/C: {quotation.customer_account_number}</div>
            )}
            {quotation.customer_address && (
              <div>{quotation.customer_address}</div>
            )}
            {quotation.customer_contact && (
              <div>Tel: {quotation.customer_contact}</div>
            )}
            {quotation.customer_email && (
              <div>Email: {quotation.customer_email}</div>
            )}
          </div>
        </>
      )}

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Items */}
      <div className="text-[10px]">
        {quotation.items?.map((item, index) => (
          <div key={index} className="mb-1">
            <div className="truncate">{item.name}</div>
            <div className="flex justify-between pl-2">
              <span>
                {item.quantity} x R{item.unit_price?.toFixed(2)}
                {(item.discount_percent || 0) > 0 && (
                  <span> −{item.discount_percent}%</span>
                )}
              </span>
              <span>R{item.total?.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Totals */}
      <div className="text-[10px]">
        <div className="flex justify-between">
          <span>Subtotal (Excl VAT):</span>
          <span>R{quotation.subtotal?.toFixed(2)}</span>
        </div>
        {(quotation.discount || 0) > 0 && (
          <div className="flex justify-between">
            <span>
              Discount
              {(quotation.customer_discount_percent || 0) > 0
                ? ` (${quotation.customer_discount_percent}%)`
                : ''}:
            </span>
            <span>-R{quotation.discount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>VAT (15%):</span>
          <span>R{quotation.tax?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-sm mt-1">
          <span>TOTAL:</span>
          <span>R{quotation.total?.toFixed(2)}</span>
        </div>
      </div>

      {/* Notes */}
      {quotation.notes && (
        <>
          <div className="border-t border-dashed border-black my-2"></div>
          <div className="text-[9px]">
            <div className="font-bold">Notes:</div>
            <div>{quotation.notes}</div>
          </div>
        </>
      )}

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Footer */}
      <div className="text-center text-[9px] mt-2">
        <div className="font-bold">QUOTATION ONLY</div>
        <div>This is not a tax invoice</div>
        <div>Prices valid for {quotation.valid_days} days</div>
        <div className="mt-2">{companyInfo.name}</div>
        <div>{companyInfo.email}</div>
      </div>
    </div>
  );
};

// A4 Quote Component
const A4Quote = ({ quotation, formatDate, formatValidUntil, hasCustomerDetails, companyInfo }) => {
  // Build customer address string
  const buildCustomerAddress = () => {
    const parts = [];
    if (quotation.customer_address) parts.push(quotation.customer_address);
    if (quotation.customer_suburb) parts.push(quotation.customer_suburb);
    if (quotation.customer_province) parts.push(quotation.customer_province);
    return parts;
  };

  return (
    <div className="quotation" style={{ minHeight: '800px' }}>
      {/* Header with Logo */}
      <div className="flex justify-between mb-8 pb-4 border-b-2 border-gray-300">
        <div className="company-info">
          <img 
            src={companyInfo.logo} 
            alt={companyInfo.name}
            style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
            className="mb-2"
          />
          <p className="text-gray-600">{companyInfo.address}</p>
          <p className="text-gray-600">{companyInfo.city}</p>
          <p className="text-gray-600">Tel: {companyInfo.phone}</p>
          {companyInfo.storeNumber && (
            <p className="text-gray-600">Store: {companyInfo.storeNumber}</p>
          )}
          <p className="text-gray-600">{companyInfo.vatNumber}</p>
          <p className="text-gray-600">{companyInfo.email}</p>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-blue-600">QUOTATION</h2>
          <p className="text-lg font-semibold mt-2">{quotation.quotation_number}</p>
          <p className="text-gray-600 mt-4">Date: {formatDate(quotation.created_at)}</p>
          <p className="text-gray-600">Valid Until: {formatValidUntil(quotation.valid_until)}</p>
          <div className={`mt-2 inline-block px-3 py-1 rounded text-sm font-semibold ${
            quotation.status === "PENDING" ? "bg-yellow-100 text-yellow-800" :
            quotation.status === "ACCEPTED" ? "bg-green-100 text-green-800" :
            "bg-gray-100 text-gray-800"
          }`}>
            {quotation.status}
          </div>
        </div>
      </div>

      {/* Customer Info */}
      {hasCustomerDetails && (
        <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-gray-700 mb-2 text-lg">Quote For:</h3>
              <p className="font-semibold text-lg">{quotation.customer_name}</p>
              {buildCustomerAddress().map((line, idx) => (
                <p key={idx} className="text-gray-600">{line}</p>
              ))}
              {quotation.customer_contact && (
                <p className="text-gray-600 mt-1">Tel: {quotation.customer_contact}</p>
              )}
              {quotation.customer_email && (
                <p className="text-gray-600">Email: {quotation.customer_email}</p>
              )}
            </div>
            <div className="text-right">
              {quotation.customer_account_number && (
                <div className="mb-2">
                  <span className="text-gray-600">Account No: </span>
                  <span className="font-semibold text-lg">{quotation.customer_account_number}</span>
                </div>
              )}
              {quotation.customer_vat_number && (
                <div>
                  <span className="text-gray-600">VAT No: </span>
                  <span className="font-semibold">{quotation.customer_vat_number}</span>
                </div>
              )}
              <div className="mt-2">
                <span className={`px-2 py-1 rounded text-sm ${
                  quotation.quote_type === "ACCOUNT QUOTE" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                }`}>
                  {quotation.quote_type}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <table className="w-full mb-6">
        <thead>
          <tr className="bg-blue-50">
            <th className="text-left p-3 border-b-2">Description</th>
            <th className="text-right p-3 border-b-2">Qty</th>
            <th className="text-right p-3 border-b-2">Unit Price</th>
            <th className="text-right p-3 border-b-2">Disc %</th>
            <th className="text-right p-3 border-b-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {quotation.items?.map((item, index) => (
            <tr key={index} className="border-b">
              <td className="p-3">{item.name}</td>
              <td className="p-3 text-right">{item.quantity}</td>
              <td className="p-3 text-right">R {item.unit_price?.toFixed(2)}</td>
              <td className="p-3 text-right">
                {(item.discount_percent || 0) > 0
                  ? `${(item.discount_percent).toFixed(item.discount_percent % 1 ? 2 : 0)}%`
                  : '-'}
              </td>
              <td className="p-3 text-right">R {item.total?.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-72">
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Subtotal (Excl VAT):</span>
            <span>R {quotation.subtotal?.toFixed(2)}</span>
          </div>
          {(quotation.discount || 0) > 0 && (
            <div className="flex justify-between py-2 border-b text-red-600">
              <span className="text-gray-600">
                Discount
                {(quotation.customer_discount_percent || 0) > 0
                  ? ` (${quotation.customer_discount_percent}% customer)`
                  : ''}:
              </span>
              <span>-R {quotation.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">VAT (15%):</span>
            <span>R {quotation.tax?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-3 text-xl font-bold border-t-2 border-black mt-2">
            <span>TOTAL:</span>
            <span className="text-blue-600">R {quotation.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {quotation.notes && (
        <div className="mt-6 p-4 bg-gray-50 rounded">
          <h3 className="font-bold text-gray-700 mb-2">Notes & Terms:</h3>
          <p className="text-gray-600 whitespace-pre-wrap">{quotation.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 pt-4 border-t">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="text-gray-500 text-sm">
              <strong>Terms & Conditions:</strong>
            </p>
            <ul className="text-gray-500 text-sm mt-2 list-disc list-inside">
              <li>This quotation is valid for {quotation.valid_days} days from the date of issue</li>
              <li>Prices are subject to change without notice after validity period</li>
              <li>E&OE - Errors and Omissions Excepted</li>
            </ul>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-sm mb-4">Accepted by:</p>
            <div className="border-b border-gray-400 w-48 ml-auto mb-2"></div>
            <p className="text-gray-500 text-sm">Signature & Date</p>
          </div>
        </div>
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>
            {[companyInfo.name, companyInfo.phone, companyInfo.storeNumber && `Store: ${companyInfo.storeNumber}`, companyInfo.email]
              .filter(Boolean).join(" | ")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotationReceiptModal;
